import React, { useRef, useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Typography,
  Divider,
  FormControlLabel,
  Checkbox,
  Alert,
  Table,
  TableBody,
  TableRow,
  TableCell,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel
} from '@mui/material';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import { DialogMode } from '../type/DialogMode';
import { Enterprise, Board, User } from '../type';
import { CloseButton } from './CloseButton';
import { CancelButton } from './CancelButton';
import { uploadImage } from '../service/imageService';
import { Timestamp } from '@firebase/firestore';
import { uploadHtml } from '../service/htmlService';
import { sortedUsers, waitForHtmlContentUrl } from '../utils/util';
import PostEditor from './PostEditor';
import { renderEnterpriseTree } from '../hook/useEnterpriseTree';

interface BoardDialogProps {
  open: boolean;
  mode: DialogMode;
  board?: Board;
  onClose: () => void;
  onSave: (board: Board) => void;
  onDelete?: (boardId: string) => void;
  i_users: User[];
  e_users: User[];
  ent: Enterprise[];
}

const BoardDialog: React.FC<BoardDialogProps> = React.memo(({
  open,
  mode,
  board,
  onClose,
  onSave,
  onDelete,
  i_users,
  e_users,
  ent,
}) => {
  const [title, setTitle] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [htmlContent, setHtmlContent] = useState<string | null>(null);
  const zipInputRef = useRef<HTMLInputElement>(null);

  const [independentUsers, setIndependentUsers] = useState<User[]>([]);
  const [selectedIndependentUsers, setSelectedIndependentUsers] = useState<User[]>([]);
  const [enterpriseUsers, setEnterpriseUsers] = useState<User[]>([]);
  const [enterprises, setEnterprises] = useState<Enterprise[]>([]);
  const [selectedEnterpriseUsers, setSelectedEnterpriseUsers] = useState<User[]>([]);
  
  // Set initial values when Dialog opens
  React.useEffect(() => {
    if (open) {
      if (mode === DialogMode.EDIT && board) {
        setTitle(board.title);
        setHtmlContent(board.content);
        setIsPublic(board.isPublic !== false);
        setEnterprises(ent);
        const tempSelectedUsers = board.allowedUsers ? i_users.filter(u => board.allowedUsers?.includes(u.uid) && !u.isEnterpriseUser) : [];
        const tempUsers = board.allowedUsers ? 
                          i_users.filter(u => !board.allowedUsers?.includes(u.uid)) : 
                          tempSelectedUsers?.length > 0 ? i_users.filter(u => tempSelectedUsers.some(selected => selected.uid !== u.uid)) : i_users;
        setSelectedIndependentUsers(sortedUsers(tempSelectedUsers));
        setIndependentUsers(sortedUsers(tempUsers));

        const tempSelectedEnterpriseUsers = [...(board.allowedEnterprises ? e_users.filter(u => u.enterpriseId && board.allowedEnterprises?.includes(u.enterpriseId)) : []), ...(e_users.filter(u => board.allowedUsers?.includes(u.uid) && u.isEnterpriseUser))];
        const tempEnterpriseUsers = board.allowedEnterprises ? 
                                    e_users.filter(u => u.enterpriseId && !board.allowedEnterprises?.includes(u.enterpriseId) && !board.allowedUsers?.includes(u.uid)) : 
                                    tempSelectedEnterpriseUsers?.length > 0 ? e_users.filter(u => tempSelectedEnterpriseUsers.some(selected => selected.uid !== u.uid)) : e_users;
        setSelectedEnterpriseUsers(sortedUsers(tempSelectedEnterpriseUsers));
        setEnterpriseUsers(sortedUsers(tempEnterpriseUsers));
      } else {
        setTitle('');
        setHtmlContent('');
        setIsPublic(true);
        setSelectedIndependentUsers([]);
        setSelectedEnterpriseUsers([]);
        if (mode === DialogMode.CREATE) {
          setEnterprises(ent);
          const tempSelectedUsers = i_users.filter(u => !u.isEnterpriseUser);
          const tempUsers = tempSelectedUsers?.length > 0 ? i_users.filter(u => tempSelectedUsers.some(selected => selected.uid !== u.uid)) : i_users;
          setIndependentUsers(sortedUsers(tempUsers));

          const tempSelectedEnterpriseUsers = [...(e_users.filter(u => u.enterpriseId)), ...(e_users.filter(u => u.isEnterpriseUser))];
          const tempEnterpriseUsers = tempSelectedEnterpriseUsers?.length > 0 ? e_users.filter(u => tempSelectedEnterpriseUsers.some(selected => selected.uid !== u.uid)) : e_users;
          setEnterpriseUsers(sortedUsers(tempEnterpriseUsers));
        } else {
          setEnterprises([]);
          setIndependentUsers([]);
          setEnterpriseUsers([]);
        }
      }
    }
  }, [open, mode, board]);

  React.useEffect(() => {
        console.log("selectedIndependentUsers changed:", selectedIndependentUsers);
  }, [selectedIndependentUsers]);
  React.useEffect(() => {
        console.log("independentUsers changed:", independentUsers);
  }, [independentUsers]);

  const handleSave = useCallback(async () => {
    setUploadError(null);
    
    if (!title.trim()) {
      alert('제목을 입력해주세요.');
      return;
    }

    // 간단한 HTML 생성
    const allowedEnterprises: string[] = [];
    const tmpEnterpriseUsers = isPublic ? [] : [...selectedEnterpriseUsers];
    for (let i = tmpEnterpriseUsers.length - 1; i >= 0; i--) {
      const user = tmpEnterpriseUsers[i];
      if (user.enterpriseId) {
        const fullySelected = isEnterpriseFullySelected(user.enterpriseId!);
        if (fullySelected) {
          // 해당 기업의 ID를 추가
          if (!allowedEnterprises.includes(user.enterpriseId)) {
            allowedEnterprises.push(user.enterpriseId);
          }
          tmpEnterpriseUsers.splice(i, 1); // 기업 사용자 목록에서 제거
        }
      } else {
        tmpEnterpriseUsers.splice(i, 1); // 기업 사용자 목록에서 제거
      }
    }
    const allowedUsers = isPublic ? [] : [...selectedIndependentUsers, ...tmpEnterpriseUsers].map(u => u.uid);

    const boardDoc: Board = {
      boardId: mode === DialogMode.EDIT && board ? board.boardId : `board_${Date.now()}`,
      title: title.trim(),
      content: htmlContent ?? "",
      isPublic,
      allowedUsers,
      allowedEnterprises,
      createdAt: mode === DialogMode.CREATE ? Timestamp.now() : board!.createdAt,
    };

    onSave(boardDoc);
    handleClose();
  }, [title, htmlContent, isPublic, mode, board, onSave, selectedIndependentUsers, selectedEnterpriseUsers]);

  function isEnterpriseFullySelected(enterpriseId: string) {
    const remaining = enterpriseUsers.filter(user => user.enterpriseId === enterpriseId);
    return remaining.length === 0;
  }
  
  const handleDelete = useCallback(() => {
    if (mode === DialogMode.EDIT && board?.boardId && onDelete) {
      onDelete(board.boardId);
      handleClose();
    }
  }, [mode, board, onDelete]);

  const handleClose = useCallback(() => {
    setTitle('');
    setHtmlContent('');
    setIsPublic(true);
    setUploadError(null);
    onClose();
  }, [onClose]);

  const handleTitleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
  }, []);

  const handlePublicChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setIsPublic(e.target.checked);
  }, []);

  const getDialogTitle = () => {
    return mode === DialogMode.CREATE ? '새 게시글 작성' : '게시글 수정';
  };

  const getSaveButtonText = () => {
    return mode === DialogMode.CREATE ? '저장' : '수정';
  };
/*
  const renderEnterpriseTree = (
    users: User[],
    isSelectedTree: boolean
  ) => {
    const grouped = users.reduce((acc, user) => {
      const eid = user.enterpriseId!;
      if (!acc[eid]) acc[eid] = [];
      acc[eid].push(user);
      return acc;
    }, {} as Record<string, User[]>);

    return (
      <SimpleTreeView>
        {Object.entries(grouped).map(([enterpriseId, members]) => (
          <TreeItem
            itemId={`enterprise-${enterpriseId}`} 
            // label={enterprises.find(e => e.enterpriseId === enterpriseId)?.enterpriseName} key={enterpriseId}
            label={
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography>{enterprises.find(e => e.enterpriseId === enterpriseId)?.enterpriseName}</Typography>
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ minWidth: '60px', padding: '4px 8px' }}
                  onClick={() => {
                    const enterpriseUsersToMove = members;
                    if (isSelectedTree) {
                      setSelectedEnterpriseUsers(prev =>
                        sortedUsers(prev.filter(u => u.enterpriseId !== enterpriseId))
                      );
                      setEnterpriseUsers(prev => sortedUsers([...prev, ...enterpriseUsersToMove]));
                    } else {
                      setEnterpriseUsers(prev =>
                        sortedUsers(prev.filter(u => u.enterpriseId !== enterpriseId))
                      );
                      setSelectedEnterpriseUsers(prev => sortedUsers([...prev, ...enterpriseUsersToMove]));
                    }
                  }}
                >
                  {isSelectedTree ? '기업 미선택' : '기업 선택'}
                </Button>
              </Box>
            }
          >
            {members.map(user => (
              <TreeItem
                itemId={`user-${user.uid}`}
                label={
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography>{user.uid}</Typography>
                    <Typography>{user.displayName}</Typography>
                    <Typography>{user.phoneNumber}</Typography>
                    <Button
                      size="small"
                      sx={{ minWidth: '60px', padding: '4px 8px' }}
                      onClick={() => {
                        if (isSelectedTree) {
                          setSelectedEnterpriseUsers(sortedUsers(selectedEnterpriseUsers.filter(u => u.uid !== user.uid)));
                          setEnterpriseUsers(sortedUsers([...enterpriseUsers, user]));
                        } else {
                          setEnterpriseUsers(sortedUsers(enterpriseUsers.filter(u => u.uid !== user.uid)));
                          setSelectedEnterpriseUsers(sortedUsers([...selectedEnterpriseUsers, user]));
                        }
                    }}>
                      {isSelectedTree ? '미선택' : '선택'}
                    </Button>
                  </Box>
                }
                key={user.uid}
              />
            ))}
          </TreeItem>
        ))}
      </SimpleTreeView>
    );
  };
*/
  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
    >

      {/* Top close button and title */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, pt: 3, pb: 2 }}>
        <Typography variant="h6" sx={{ fontSize: '1.25rem' }}>
          {getDialogTitle()}
        </Typography>
        <CloseButton onClose={onClose} />
      </Box>
      <Divider />
      <DialogContent>
        <Box sx={{ display: 'flex', gap: 3 }}>
          {/* 왼쪽 컬럼: 제목, 배너 이미지, 업로드 타입 선택 */}
          <Box
            sx={{
              flex: 0.9,
              display: 'flex', 
              flexDirection: 'column', 
              gap: 1, 
              mt: 0.5,
              border: '1px solid #ccc',
              borderRadius: 2,
              padding: 2,
              height: 500,         // 👈 fixed height (px, rem, %, vh possible)
              overflow: 'auto',    // 👈 scroll if content overflows
            }}>
            {uploadError && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {uploadError}
              </Alert>
            )}
            <TextField
              label="제목"
              value={title}
              onChange={handleTitleChange}
              fullWidth
            />

            <PostEditor htmlContent={htmlContent} onChange={setHtmlContent} />
          </Box>

          {/* 오른쪽 컬럼: 공개 여부 및 사용자 선택 */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isPublic}
                  onChange={handlePublicChange}
                />
              }
              label="전체 공개"
            />

            {isPublic ? (
              <Typography variant="body2" color="textSecondary">
                모든 사용자가 이 공지를 볼 수 있습니다.
              </Typography>
            ) : (
              <>
              <Typography variant="body2" color="textSecondary">
                공지 볼 수 있는 사용자를 선택하세요.
              </Typography>
              <Box
                sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: 3, 
                  mt: 3,
                  border: '1px solid #ccc',
                  borderRadius: 2,
                  padding: 2,
                }}>
                {/* 미선택 사용자 */}
                <Box>
                  <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>미선택 개인 사용자</Typography>

                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => {
                        setSelectedIndependentUsers(prev => sortedUsers([...prev, ...independentUsers]));
                        setIndependentUsers([]);
                      }}
                    >
                      전체 선택
                    </Button>
                  </Box>
                  <Table size="small">
                    <TableBody>
                      {independentUsers.map(user => (
                        <TableRow key={user.uid}>
                          <TableCell>{user.uid}</TableCell>
                          <TableCell>{user.displayName}</TableCell>
                          <TableCell>{user.phoneNumber}</TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              sx={{ minWidth: '60px', padding: '4px 8px' }}
                              onClick={() => {
                                setIndependentUsers(sortedUsers(independentUsers.filter(u => u.uid !== user.uid)));
                                setSelectedIndependentUsers(sortedUsers([...selectedIndependentUsers, user]));
                            }}>선택</Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>

                <Divider sx={{ borderBottomWidth: '2px', borderColor: '#444' }} />

                {/* 선택된 사용자 */}
                <Box>
                  <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>선택된 개인 사용자</Typography>

                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setIndependentUsers(prev => sortedUsers([...prev, ...selectedIndependentUsers]));
                        setSelectedIndependentUsers([]);
                      }}
                    >
                      전체 미선택
                    </Button>
                  </Box>
                  <Table size="small">
                    <TableBody>
                      {selectedIndependentUsers.map(user => (
                        <TableRow key={user.uid}>
                          <TableCell>{user.uid}</TableCell>
                          <TableCell>{user.displayName}</TableCell>
                          <TableCell>{user.phoneNumber}</TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              sx={{ minWidth: '60px', padding: '4px 8px' }}
                              onClick={() => {
                                setSelectedIndependentUsers(sortedUsers(selectedIndependentUsers.filter(u => u.uid !== user.uid)));
                                setIndependentUsers(sortedUsers([...independentUsers, user]));
                            }}>미선택</Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </Box>

              <Box 
                sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: 3, 
                  mt: 3,
                  border: '1px solid #ccc',
                  borderRadius: 2,
                  padding: 2,
                }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>미선택 기업 사용자</Typography>
                  {/* {renderEnterpriseTree(enterpriseUsers, false)} */}
                  {renderEnterpriseTree(enterpriseUsers, enterprises, false, selectedEnterpriseUsers, setSelectedEnterpriseUsers, enterpriseUsers, setEnterpriseUsers)}
                </Box>

                <Divider sx={{ borderBottomWidth: '2px', borderColor: '#444' }} />

                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>선택된 기업 사용자</Typography>
                  {/* {renderEnterpriseTree(selectedEnterpriseUsers, true)} */}
                  {renderEnterpriseTree(selectedEnterpriseUsers, enterprises, true, selectedEnterpriseUsers, setSelectedEnterpriseUsers, enterpriseUsers, setEnterpriseUsers)}
                </Box>
              </Box>
              </>
            )}
          </Box>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2, pt: 1, justifyContent: 'space-between' }}>
        <Box>
          {mode === DialogMode.EDIT && board?.boardId && onDelete && (
            <Button 
              onClick={handleDelete}
              sx={{ 
                backgroundColor: '#EC2D4D', 
                '&:hover': {
                  backgroundColor: '#F26C82'
                }
              }}
            >
              삭제
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <CancelButton onClick={handleClose}>닫기</CancelButton>
          <Button 
            onClick={handleSave}
            disabled={isUploading}
          >
            {isUploading ? '업로드 중...' : getSaveButtonText()}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
});

export default BoardDialog; 